import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is already logged in (e.g., from localStorage)
        const token = localStorage.getItem('vetToken');
        const userData = localStorage.getItem('vetUser');
        
        if (token && userData) {
          try {
            // Check if token is expired
            const tokenPayload = JSON.parse(atob(token.split('.')[1]));
            const currentTime = Date.now() / 1000;
            
            if (tokenPayload.exp && tokenPayload.exp < currentTime) {
              // Token is expired, try to refresh it
              await refreshTokenSilently(token);
            } else {
              // Token is still valid - fetch fresh user data from backend to get latest fields (like language)
              try {
                const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
                const userResp = await fetch(`${API_BASE}/api/user/me`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (userResp.ok) {
                  const freshUserData = await userResp.json();
                  // Update localStorage with fresh user data
                  localStorage.setItem('vetUser', JSON.stringify(freshUserData));
                  setUser(freshUserData);
                  setIsAuthenticated(true);
                  
                  // Update i18n language from fresh user data
                  if (freshUserData?.language) {
                    const i18nInstance = require('../i18n').default;
                    if (i18nInstance && i18nInstance.language !== freshUserData.language) {
                      i18nInstance.changeLanguage(freshUserData.language);
                    }
                  }
                } else {
                  // If fetching fresh data fails, use cached data as fallback
                  const parsedUser = JSON.parse(userData);
                  setUser(parsedUser);
                  setIsAuthenticated(true);
                  
                  if (parsedUser?.language) {
                    const i18nInstance = require('../i18n').default;
                    if (i18nInstance && i18nInstance.language !== parsedUser.language) {
                      i18nInstance.changeLanguage(parsedUser.language);
                    }
                  }
                }
              } catch (error) {
                console.error('Error fetching fresh user data:', error);
                // Fallback to cached data
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
                setIsAuthenticated(true);
              }
              
              // Set up periodic token refresh (every 7 days)
              const refreshInterval = setInterval(() => {
                const currentToken = localStorage.getItem('vetToken');
                if (currentToken) {
                  refreshTokenSilently(currentToken);
                }
              }, 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
              
              // Cleanup interval on unmount
              return () => clearInterval(refreshInterval);
            }
          } catch (error) {
            console.error('Error parsing user data or token:', error);
            localStorage.removeItem('vetToken');
            localStorage.removeItem('vetUser');
          }
        }
      } catch (error) {
        console.error('Error during auth initialization:', error);
      } finally {
        // Always set loading to false when done
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const refreshTokenSilently = async (token) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
      const resp = await fetch(`${API_BASE}/api/auth/refresh-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      
      if (resp.ok) {
        const data = await resp.json();
        localStorage.setItem('vetToken', data.token);
        localStorage.setItem('vetUser', JSON.stringify(data.user));
        setUser(data.user);
        setIsAuthenticated(true);
        
        // Update i18n language from user data
        if (data.user?.language) {
          const i18nInstance = require('../i18n').default;
          if (i18nInstance && i18nInstance.language !== data.user.language) {
            i18nInstance.changeLanguage(data.user.language);
          }
        }
      } else {
        // Refresh failed, clear storage
        localStorage.removeItem('vetToken');
        localStorage.removeItem('vetUser');
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      localStorage.removeItem('vetToken');
      localStorage.removeItem('vetUser');
    }
  };

  const sendVetLoginCode = async (email) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
      const resp = await fetch(`${API_BASE}/api/auth/vet-login-send-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.msg || 'Failed to send verification code');
      }
      return { success: true, message: data.msg };
    } catch (error) {
      console.error('Send vet login code error:', error);
      return { success: false, error: error.message };
    }
  };

  const verifyVetLoginCode = async (email, code) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
      const resp = await fetch(`${API_BASE}/api/auth/vet-login-verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.msg || 'Invalid verification code');
      }
      const loggedUser = data?.user;
      const token = data?.token;
      if (!loggedUser || !token) {
        throw new Error('Invalid response from server');
      }
      // Allow only vets (2) and admins (10)
      if (loggedUser.userRole !== 2 && loggedUser.userRole !== 10) {
        return { success: false, error: 'Access restricted to registered vets and admins.' };
      }
      localStorage.setItem('vetToken', token);
      localStorage.setItem('vetUser', JSON.stringify(loggedUser));
      setUser(loggedUser);
      setIsAuthenticated(true);
      
      // Update i18n language from user data
      if (loggedUser?.language) {
        const i18nInstance = require('../i18n').default;
        if (i18nInstance && i18nInstance.language !== loggedUser.language) {
          i18nInstance.changeLanguage(loggedUser.language);
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Verify vet login code error:', error);
      return { success: false, error: error.message };
    }
  };

  const login = async (credentials) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3000';
      const resp = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: credentials.email, password: credentials.password })
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data?.msg || 'Login failed');
      }
      const loggedUser = data?.user;
      const token = data?.token;
      if (!loggedUser || !token) {
        throw new Error('Invalid response from server');
      }
      // Allow only vets (2) and admins (10)
      if (loggedUser.userRole !== 2 && loggedUser.userRole !== 10) {
        return { success: false, error: 'Access restricted to registered vets and admins.' };
      }
      localStorage.setItem('vetToken', token);
      localStorage.setItem('vetUser', JSON.stringify(loggedUser));
      setUser(loggedUser);
      setIsAuthenticated(true);
      
      // Update i18n language from user data
      if (loggedUser?.language) {
        const i18nInstance = require('../i18n').default;
        if (i18nInstance && i18nInstance.language !== loggedUser.language) {
          i18nInstance.changeLanguage(loggedUser.language);
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    localStorage.removeItem('vetToken');
    localStorage.removeItem('vetUser');
    setUser(null);
    setIsAuthenticated(false);
  };

  const value = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    sendVetLoginCode,
    verifyVetLoginCode,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
